<template>
  <DataCard
    :title="orderDetailsTitle"
    icon="fas fa-shopping-cart"
    iconBgColor="bg-purple-100"
    iconColor="text-purple-600"
    :clickable="false"
    class="mb-4"
    className="max-[600px]:p-4"
  >
    <template #title-after>
      <template v-if="orderDetailData?.id_commande_groupe">
        <span
          class="block w-full whitespace-normal leading-tight text-lg font-extrabold text-blue-600 max-[600px]:block min-[601px]:hidden"
        >
          #{{ orderDetailData.id_commande_groupe }}
        </span>
        <span
          class="hidden whitespace-nowrap text-lg font-extrabold text-blue-600 min-[601px]:inline"
        >
          #{{ orderDetailData.id_commande_groupe }}
        </span>
      </template>
    </template>

    <template #header-toggle>
      <button
        v-if="collapsible"
        type="button"
        class="inline-flex items-center gap-2 rounded-lg border-2 border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 max-[600px]:h-10 max-[600px]:w-10 max-[600px]:justify-center max-[600px]:border-0 max-[600px]:p-0 max-[600px]:shadow-none"
        :aria-expanded="!isCollapsed"
        :aria-label="isCollapsed ? 'Voir les détails de la commande' : 'Masquer les détails de la commande'"
        @click.stop="toggleCollapse"
      >
        <span class="max-[600px]:hidden">{{ isCollapsed ? "Voir" : "Masquer" }}</span>
        <i
          :class="[
            'fas text-xs transition-transform duration-200',
            isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up',
          ]"
        ></i>
      </button>
    </template>

    <div
      :class="[
        'transition-all duration-300 ease-in-out',
        collapsible
          ? isCollapsed
            ? 'max-h-0 opacity-0 overflow-hidden'
            : isExpanded
              ? 'max-h-none opacity-100'
              : 'max-h-[20000px] opacity-100 overflow-hidden'
          : '',
      ]"
      @transitionend="onContentTransitionEnd"
    >
      <!-- Message de remplacement -->
      <div
        v-if="replacementMessage"
        class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4"
      >
        <p class="text-orange-800 text-sm">
          {{ replacementMessage }}
        </p>
      </div>

      <!-- Commentaires admin (visible uniquement en mode admin / impersonation) -->
      <div
        v-if="isAdmin"
        class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
      >
        <div class="font-semibold text-sm text-blue-900 mb-2">
          <i class="fas fa-comment-dots mr-1"></i> Commentaires
        </div>
        <div v-if="adminCommentaires.length" class="mb-2 space-y-2">
          <div
            v-for="c in adminCommentaires"
            :key="c.id_commande_commentaire"
            class="bg-white border border-blue-100 rounded p-2"
          >
            <div class="text-sm text-gray-800">
              <b>{{ c.prenom }} :</b> <span>{{ c.commentaire }}</span>
              <i
                v-if="c.canDelete"
                class="fas fa-trash-alt text-red-500 cursor-pointer ml-1"
                @click="askDeleteAdminCommentaire(c.id_commande_commentaire)"
              ></i>
            </div>
            <div class="text-xs text-gray-400">
              {{ formatCommentDate(c.date_creation) }}
            </div>
          </div>
        </div>
        <div>
          <textarea
            v-model="newAdminComment"
            rows="2"
            class="w-full border rounded px-2 py-1 text-sm"
            placeholder="Ajouter un commentaire..."
          ></textarea>
          <button
            type="button"
            class="mt-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
            :disabled="savingAdminComment"
            @click="saveAdminCommentaire"
          >
            Enregistrer
          </button>
        </div>
      </div>

      <!-- Product Tables -->
      <div v-if="products.length > 0" class="mb-6">
        <!-- Version Desktop -->
        <div
          class="hidden lg:block overflow-x-auto rounded border border-gray-200"
        >
          <table class="min-w-full divide-y divide-gray-100">
            <thead class="bg-gray-50/80">
              <tr>
                <th
                  class="px-2 py-1.5 text-left text-xs font-semibold text-black uppercase tracking-wider"
                >
                  Produit
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-l border-r border-gray-200"
                >
                  Taille
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200"
                >
                  Qté commandée
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200"
                >
                  Prix HT
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider"
                >
                  Poids (kg)
                </th>
                <th
                  v-if="canMarkRupture"
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-l border-gray-200"
                >
                  Rupture
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              <template v-for="group in productGroups" :key="group.referenceBase">
                <!-- Ligne de séparation par reference_base -->
                <tr class="bg-gray-100 border-t-2 border-gray-300">
                  <td :colspan="canMarkRupture ? 6 : 5" class="px-3 py-2">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-bold text-black uppercase">
                        <i class="fas fa-layer-group mr-2 text-blue-600"></i>
                        Réf. Base : {{ group.referenceBase }}
                      </span>
                      <span class="text-sm font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                        Total pièces : {{ group.totalPieces }}
                      </span>
                    </div>
                  </td>
                </tr>
                <!-- Produits du groupe -->
                <tr
                  v-for="product in group.products"
                  :key="product.id"
                  class="hover:bg-blue-50/30 transition-colors"
                >
                <!-- Produit - Image + Référence + Prix + Couleur -->
                <td class="px-2 py-1.5">
                  <div class="flex items-start gap-2">
                    <!-- Image -->
                    <div
                      class="w-32 h-32 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded border border-gray-200 overflow-hidden"
                    >
                      <img
                        v-if="getImageUrlForProduct(product)"
                        :src="getImageUrlForProduct(product)"
                        :alt="product.reference"
                        class="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        @error="handleImageError"
                        @click="
                          openImageModal(
                            getImageUrlForProduct(product),
                            product.reference,
                          )
                        "
                      />
                      <i v-else class="fas fa-tshirt text-black text-lg"></i>
                    </div>

                    <!-- Détails produit -->
                    <div class="flex-1 min-w-0">
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black">Réf:&nbsp;</span>
                        <span class="text-black">{{
                          product.reference || "-"
                        }}</span>
                      </p>
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black"
                          >Catégorie:&nbsp;</span
                        >
                        <span class="text-black">{{
                          product.categorie || "-"
                        }}</span>
                      </p>
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black">Prix:&nbsp;</span>
                        <template
                          v-if="product.hasRemise && product.priceUnitNormal"
                        >
                          <span class="text-black line-through mr-1"
                            >{{ formatPrice(product.priceUnitNormal) }}€</span
                          >
                          <span class="text-black"
                            >{{ formatPrice(product.priceUnit || 0) }}€</span
                          >
                        </template>
                        <template v-else>
                          <span class="text-black"
                            >{{ formatPrice(product.priceUnit || 0) }}€</span
                          >
                        </template>
                      </p>
                      <p
                        class="text-sm text-black leading-tight flex items-center gap-1.5 flex-wrap"
                      >
                        <span class="font-medium text-black">Couleur:&nbsp;</span>
                        <span
                          v-if="hasOfficialColorSwatch(product)"
                          class="color-swatch-dot inline-block w-3.5 h-3.5 rounded-full flex-shrink-0"
                          :style="getOfficialColorSwatchStyle(product)"
                          :title="product.colorFR || product.color || ''"
                        ></span>
                        <span class="text-black">{{
                          product.colorFR || product.color || "-"
                        }}</span>
                      </p>
                    </div>
                  </div>
                </td>

                <!-- Taille -->
                <td class="px-2 py-1.5 border-l border-r border-gray-200">
                  <div
                    v-if="getProductSizeRows(product).length"
                    class="flex justify-center gap-3"
                  >
                    <div
                      v-for="row in getProductSizeRows(product)"
                      :key="row.label"
                      class="flex flex-col items-center gap-0.5"
                    >
                      <span class="text-sm text-black">{{ row.label }}</span>
                      <span class="text-sm font-bold text-black">{{
                        row.qty
                      }}</span>
                    </div>
                  </div>
                  <span
                    v-else
                    class="block text-center text-sm font-medium text-black"
                    >—</span
                  >
                </td>

                <!-- Qté commandée : 1x 6 -->
                <td
                  class="px-2 py-1.5 whitespace-nowrap text-center border-r border-gray-200"
                >
                  <p class="text-sm font-semibold text-black">
                    {{ getOrderedQuantityLabel(product) }}
                  </p>
                </td>

                <!-- Prix HT -->
                <td
                  class="px-2 py-1.5 whitespace-nowrap text-center border-r border-gray-200"
                >
                  <p class="text-sm font-semibold text-black">
                    {{
                      formatPrice(product.priceLineHT || product.price || 0)
                    }}€
                  </p>
                </td>

                <!-- Poids (kg) -->
                <td class="px-2 py-1.5 whitespace-nowrap text-center">
                  <p class="text-sm text-black font-medium">
                    {{
                      formatWeight(product.weightTotal || product.weight || 0)
                    }}
                  </p>
                </td>
                <!-- Action - Checkbox -->
                <td
                  v-if="canMarkRupture"
                  class="px-2 py-1.5 whitespace-nowrap border-l border-gray-200 text-center"
                >
                  <input
                    type="checkbox"
                    :checked="selectedProducts.includes(product.id)"
                    @change="toggleProduct(product.id)"
                    class="w-3.5 h-3.5 text-blue-600 bg-white border-gray-300 rounded focus:ring-1 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Version Mobile/Tablette - Cartes verticales -->
        <div class="block lg:hidden space-y-2">
          <template v-for="group in productGroups" :key="group.referenceBase">
            <!-- Séparation par reference_base (mobile) -->
            <div class="bg-gray-100 border-t-2 border-gray-300 rounded-md px-3 py-2">
              <div class="flex flex-wrap items-center justify-between gap-1">
                <span class="text-sm font-bold text-black uppercase">
                  <i class="fas fa-layer-group mr-2 text-blue-600"></i>
                  Réf. Base : {{ group.referenceBase }}
                </span>
                <span class="text-sm font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                  Total pièces : {{ group.totalPieces }}
                </span>
              </div>
            </div>
          <div v-for="product in group.products" :key="product.id">
            <div
              class="bg-white border border-gray-200 rounded-md overflow-hidden"
            >
              <div class="px-3 py-1 bg-blue-50 border-b border-blue-200 min-[601px]:py-2">
                <div class="flex items-start justify-between gap-2">
                  <div
                    class="min-w-0 break-words text-sm py-1 font-bold text-blue-700 leading-tight min-[601px]:py-2"
                  >
                    Réf :
                    <span class="text-black">{{ product.reference || "-" }}</span>
                  </div>
                  <input
                    v-if="canMarkRupture"
                    type="checkbox"
                    :checked="selectedProducts.includes(product.id)"
                    :aria-label="`Sélectionner le produit ${product.reference || product.id}`"
                    @change="toggleProduct(product.id)"
                    class="w-6 h-6 mt-1 min-[601px]:mt-2 flex-shrink-0 text-blue-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                </div>
                <div class="text-sm text-black leading-tight pb-1">
                  Catégorie :
                  <span>{{ product.categorie || "-" }}</span>
                </div>
              </div>
              <div class="py-2 pr-3 pl-0 min-[601px]:p-3 hover:bg-blue-50/30 transition-colors">
                <!-- Layout horizontal : Image à gauche, Infos à droite -->
                <div class="flex gap-3">
                  <!-- Div Image -->
                  <div class="flex-shrink-0">
                    <div
                      class="w-28 h-36 min-[360px]:w-32 min-[360px]:h-44 min-[601px]:w-40 flex items-center justify-center bg-gray-50 rounded border border-gray-200 overflow-hidden"
                    >
                      <img
                        v-if="getImageUrlForProduct(product)"
                        :src="getImageUrlForProduct(product)"
                        :alt="product.reference"
                        class="w-full h-full object-contain min-[601px]:object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        @error="handleImageError"
                        @click="
                          openImageModal(
                            getImageUrlForProduct(product),
                            product.reference,
                          )
                        "
                      />
                      <i v-else class="fas fa-tshirt text-black text-lg"></i>
                    </div>
                  </div>

                  <!-- Div Infos (tout le reste) -->
                  <div class="flex-1 min-w-0">
                    <!-- Couleur -->
                    <p class="text-sm text-black mt-0.5 mb-2 flex items-center gap-1.5 flex-wrap">
                      Couleur:
                      <span
                        v-if="hasOfficialColorSwatch(product)"
                        class="color-swatch-dot inline-block w-3.5 h-3.5 rounded-full flex-shrink-0"
                        :style="getOfficialColorSwatchStyle(product)"
                        :title="product.colorFR || product.color || ''"
                      ></span>
                      {{ product.colorFR || product.color || "-" }}
                    </p>

                    <!-- Quantité / Tailles -->
                    <div class="mb-2">
                      <div
                        v-if="getProductSizeRows(product).length"
                        class="mb-2 flex flex-wrap justify-start gap-x-3 gap-y-1"
                      >
                        <div
                          v-for="row in getProductSizeRows(product)"
                          :key="row.label"
                          class="flex flex-col items-center gap-0.5"
                        >
                          <span class="text-sm text-black">{{
                            row.label
                          }}</span>
                          <span class="text-sm font-bold text-black">{{
                            row.qty
                          }}</span>
                        </div>
                      </div>
                      <div class="text-sm text-black">
                        <div class="mb-0.5">Qté commandée</div>
                        <div class="font-bold">
                          {{ getOrderedQuantityLabel(product) }}
                        </div>
                      </div>
                    </div>

                    <!-- Prix HT et total -->
                    <div class="grid grid-cols-2 gap-2 text-sm text-black">
                      <div class="min-w-0 break-words">
                        <div class="mb-0.5">Prix HT:</div>
                        <div class="flex flex-col items-start gap-0.5 font-bold">
                          <span
                            v-if="product.hasRemise && product.priceUnitNormal"
                            class="line-through"
                            >{{ formatPrice(product.priceUnitNormal) }}€</span
                          >
                          <span>{{ formatPrice(product.priceUnit || 0) }}€</span>
                        </div>
                      </div>
                      <div class="min-w-0 break-words">
                        <div class="mb-0.5">Total:</div>
                        <span class="font-bold"
                          >{{ formatPrice(product.priceLineHT || product.price || 0) }}€</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </template>
        </div>
      </div>

      <!-- Aucun produit -->
      <div v-else class="text-sm text-black py-4 mb-6">
        Aucun produit dans cette commande
      </div>

      <!-- Détails de la commande -->
      <div
        v-if="orderSummary"
        class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 mt-4"
      >
        <div class="bg-gray-50 p-2 rounded text-sm">
          <div class="space-y-1">
            <div class="flex justify-between">
              <span class="text-black"
                >Prix Total HT{{ hasReduction ? " avant remise" : "" }} (€)
                :</span
              >
              <span class="text-black">{{
                formatPrice(montantTotal || 0)
              }}</span>
            </div>
            <div
              v-if="hasReduction && montantApresRemiseDisplay > 0"
              class="flex justify-between"
            >
              <span class="text-black">Remise HT (€) :</span>
              <span class="text-black"
                >- {{ formatPrice(montantTotal - montantApresRemiseDisplay) }}
              </span>
            </div>
            <div
              v-if="hasReduction && montantApresRemiseDisplay > 0"
              class="flex justify-between"
            >
              <span class="text-black">Prix Total HT après remise (€) :</span>
              <span class="text-black">{{
                formatPrice(montantApresRemiseDisplay)
              }}</span>
            </div>
            <div v-if="!isEtranger" class="flex justify-between">
              <span class="text-black">TVA ({{ tvaRate * 100 }}%) (€) :</span>
              <span class="text-black">{{ formatPrice(tvaAmount || 0) }}</span>
            </div>
            <div v-if="!isEtranger" class="flex justify-between">
              <span class="text-black">Prix total (€ TTC) :</span>
              <span class="text-black">{{ formatPrice(totalTTC || 0) }}</span>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 p-2 rounded text-sm">
          <div class="space-y-1">
            <div class="flex justify-between">
              <span class="text-black">Quantité totale :</span>
              <span class="text-black">{{
                orderSummary.quantityTotal || 0
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Poids total (kg):</span>
              <span class="text-black">{{
                formatWeight(orderSummary.weight || 0)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-black">Nombre de colis :</span>
              <span class="text-black">{{ orderSummary.packages || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Détail Remise / Code Promo -->
      <div v-if="remises && remises.length > 0" class="mb-4">
        <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div class="bg-gray-50 px-3 py-2 border-b border-gray-200">
            <h4 class="text-sm font-semibold text-black">
              Détail Remise / Code Promo
            </h4>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-3 py-2 text-left text-sm font-semibold text-black uppercase"
                  >
                    Libelle / Code
                  </th>
                  <th
                    class="px-3 py-2 text-left text-sm font-semibold text-black uppercase"
                  >
                    Remise / Code promo
                  </th>
                  <th
                    class="px-3 py-2 text-left text-sm font-semibold text-black uppercase"
                  >
                    Auteur
                  </th>
                  <th
                    class="px-3 py-2 text-left text-sm font-semibold text-black uppercase"
                  >
                    Type
                  </th>
                  <th
                    class="px-3 py-2 text-left text-sm font-semibold text-black uppercase"
                  >
                    Montant
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <!-- Remises sur commandes -->
                <template
                  v-for="remise in remisesCommandes"
                  :key="remise.id_commande_remise"
                >
                  <tr>
                    <td class="px-3 py-2 text-sm text-black">
                      {{ remise.libelle || "-" }}
                    </td>
                    <td class="px-3 py-2 text-sm text-black">
                      {{ remise.typeRemise }}
                    </td>
                    <td class="px-3 py-2 text-sm text-black">
                      {{ remise.auteur }}
                    </td>
                    <td class="px-3 py-2 text-sm text-black">
                      {{ remise.type }}
                    </td>
                    <td class="px-3 py-2 text-sm text-red-600 font-medium">
                      {{ remise.montant }}
                    </td>
                  </tr>
                  <tr v-if="remise.auteur === 'Efashion'">
                    <td colspan="5" class="px-3 py-2">
                      <div
                        class="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-800"
                      >
                        Cette commande bénéficie d'une remise Efashion que vous
                        devez appliquer dans la facture. Le montant sera
                        automatiquement déduit de la commission d'efashion sur
                        la commande
                      </div>
                    </td>
                  </tr>
                </template>

                <!-- Remises sur frais de port -->
                <template v-if="remisesFraisPort.length > 0">
                  <tr>
                    <td colspan="5" class="px-3 py-2 text-center bg-gray-100">
                      <strong class="text-sm text-black"
                        >Partie Frais de port</strong
                      >
                    </td>
                  </tr>
                  <template
                    v-for="remise in remisesFraisPort"
                    :key="remise.id_commande_remise"
                  >
                    <tr>
                      <td class="px-3 py-2 text-sm text-black">
                        {{ remise.libelle || "-" }}
                      </td>
                      <td class="px-3 py-2 text-sm text-black">
                        {{ remise.typeRemise }}
                      </td>
                      <td class="px-3 py-2 text-sm text-black">
                        {{ remise.auteur }}
                      </td>
                      <td class="px-3 py-2 text-sm text-black">
                        {{ remise.type }}
                      </td>
                      <td class="px-3 py-2 text-sm text-red-600 font-medium">
                        {{ remise.montant }}
                      </td>
                    </tr>
                    <tr v-if="remise.auteur === 'Efashion'">
                      <td colspan="5" class="px-3 py-2">
                        <div
                          class="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-800"
                        >
                          Cette commande bénéficie d'une remise Efashion que
                          vous devez appliquer dans la facture. Le montant sera
                          automatiquement déduit de la commission d'efashion sur
                          la commande
                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Bouton Rupture - seulement si statut == 1 (en attente) -->
      <div v-if="canMarkRupture" class="flex gap-4 mt-4">
        <button
          @click="openDiviserModal"
          :disabled="dividing || selectedProducts.length === 0"
          :aria-busy="dividing"
          :class="[
            'inline-flex items-center gap-2 px-4 py-2 rounded-md transition-colors disabled:opacity-60',
            selectedProducts.length === 0
              ? 'bg-gray-300 text-black cursor-not-allowed'
              : 'bg-red-600 text-white hover:bg-red-700',
          ]"
          type="button"
        >
          <i v-if="dividing" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
          {{ dividing ? 'Traitement…' : 'Rupture' }}
        </button>
      </div>

      <OrderReplacements
        ref="replacementPanel"
        :order-id="orderId"
        :products="productsRupture"
        :can-propose="canProposeReplacement"
        @settled="replacedLineIds = $event"
        @changed="onReplacementChanged"
        @replacement-confirmation="emit('replacementConfirmation', $event)"
      />

      <!-- Section Produits en rupture -->
      <div v-if="productsRupture.length > 0" class="mt-6">
        <h4 class="text-sm font-semibold text-red-600 mb-3">
          Références en rupture :
        </h4>
        <p v-if="replacementUnavailableReason" class="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="status">
          {{ replacementUnavailableReason }}
        </p>

        <!-- Version Desktop Rupture -->
        <div
          class="hidden lg:block overflow-x-auto rounded border border-gray-200"
        >
          <table class="min-w-full divide-y divide-gray-100">
            <thead class="bg-gray-50/80">
              <tr>
                <th
                  class="px-2 py-1.5 text-left text-xs font-semibold text-black uppercase tracking-wider"
                >
                  Produit
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-l border-r border-gray-200"
                >
                  Taille
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200"
                >
                  Qté commandée
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200"
                >
                  Prix HT
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider"
                >
                  Poids (kg)
                </th>
                <th
                  class="px-2 py-1.5 text-center text-xs font-semibold text-black uppercase tracking-wider border-l border-gray-200"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              <tr
                v-for="product in productsRupture"
                :key="product.id"
                class="bg-red-100 hover:bg-red-200 transition-colors"
              >
                <!-- Produit - Image + Référence + Prix + Couleur -->
                <td class="px-2 py-1.5">
                  <div class="flex items-start gap-2">
                    <!-- Image -->
                    <div
                      class="w-32 h-32 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded border border-gray-200 overflow-hidden"
                    >
                      <img
                        v-if="getImageUrlForProduct(product)"
                        :src="getImageUrlForProduct(product)"
                        :alt="product.reference"
                        class="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        @error="handleImageError"
                        @click="
                          openImageModal(
                            getImageUrlForProduct(product),
                            product.reference,
                          )
                        "
                      />
                      <i v-else class="fas fa-tshirt text-black text-lg"></i>
                    </div>

                    <!-- Détails produit -->
                    <div class="flex-1 min-w-0">
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black">Réf:&nbsp;</span>
                        <span class="text-black">{{
                          product.reference || "-"
                        }}</span>
                      </p>
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black"
                          >Catégorie:&nbsp;</span
                        >
                        <span class="text-black">{{
                          product.categorie || "-"
                        }}</span>
                      </p>
                      <p class="text-sm text-black leading-tight mb-0.5">
                        <span class="font-medium text-black">Prix:&nbsp;</span>
                        <template
                          v-if="product.hasRemise && product.priceUnitNormal"
                        >
                          <span class="text-black line-through mr-1"
                            >{{ formatPrice(product.priceUnitNormal) }}€</span
                          >
                          <span class="text-black"
                            >{{ formatPrice(product.priceUnit || 0) }}€</span
                          >
                        </template>
                        <template v-else>
                          <span class="text-black"
                            >{{ formatPrice(product.priceUnit || 0) }}€</span
                          >
                        </template>
                      </p>
                      <p
                        class="text-sm text-black leading-tight flex items-center gap-1.5 flex-wrap"
                      >
                        <span class="font-medium text-black">Couleur:&nbsp;</span>
                        <span
                          v-if="hasOfficialColorSwatch(product)"
                          class="color-swatch-dot inline-block w-3.5 h-3.5 rounded-full flex-shrink-0"
                          :style="getOfficialColorSwatchStyle(product)"
                          :title="product.colorFR || product.color || ''"
                        ></span>
                        <span class="text-black">{{
                          product.colorFR || product.color || "-"
                        }}</span>
                      </p>
                    </div>
                  </div>
                </td>

                <!-- Taille -->
                <td class="px-2 py-1.5 border-l border-r border-gray-200">
                  <div
                    v-if="getProductSizeRows(product).length"
                    class="flex justify-center gap-3"
                  >
                    <div
                      v-for="row in getProductSizeRows(product)"
                      :key="row.label"
                      class="flex flex-col items-center gap-0.5"
                    >
                      <span class="text-sm text-black">{{ row.label }}</span>
                      <span class="text-sm font-bold text-black">{{
                        row.qty
                      }}</span>
                    </div>
                  </div>
                  <span
                    v-else
                    class="block text-center text-sm font-medium text-black"
                    >—</span
                  >
                </td>

                <!-- Qté commandée : 1x 6 -->
                <td
                  class="px-2 py-1.5 whitespace-nowrap text-center border-r border-gray-200"
                >
                  <p class="text-sm font-semibold text-black">
                    {{ getOrderedQuantityLabel(product) }}
                  </p>
                </td>

                <!-- Prix HT -->
                <td
                  class="px-2 py-1.5 whitespace-nowrap text-center border-r border-gray-200"
                >
                  <p class="text-sm font-semibold text-black">
                    {{
                      formatPrice(product.priceLineHT || product.price || 0)
                    }}€
                  </p>
                </td>

                <!-- Poids (kg) -->
                <td class="px-2 py-1.5 whitespace-nowrap text-center">
                  <p class="text-sm text-black font-medium">
                    {{
                      formatWeight(product.weightTotal || product.weight || 0)
                    }}
                  </p>
                </td>
                <!-- Action - Checkbox -->
                <td
                  class="px-2 py-1.5 whitespace-nowrap border-l border-gray-200 text-center"
                >
                  <input
                    v-if="canReactivate && !replacedLineIds.includes(product.id)"
                    type="checkbox"
                    :checked="selectedProductsRupture.includes(product.id)"
                    @change="toggleProductRupture(product.id)"
                    class="w-3.5 h-3.5 text-blue-600 bg-white border-gray-300 rounded focus:ring-1 focus:ring-blue-500 cursor-pointer"
                  />
                  <span v-if="replacedLineIds.includes(product.id)" class="block text-xs text-indigo-700">Proposé / remplacé — voir la proposition</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Version Mobile/Tablette - Cartes verticales Rupture -->
        <div class="block lg:hidden space-y-2">
          <div v-for="product in productsRupture" :key="product.id">
            <div
              class="bg-red-50 border border-red-200 rounded-md overflow-hidden"
            >
              <div class="px-3 py-1 bg-red-100 border-b border-red-200 min-[601px]:py-2">
                <div class="flex items-start justify-between gap-2">
                  <div
                    class="min-w-0 break-words text-[11px] py-1 font-bold text-red-700 leading-tight min-[601px]:py-2"
                  >
                    Réf:&nbsp;
                    <span class="text-black">{{ product.reference || "-" }}</span>
                  </div>
                  <input
                    v-if="canReactivate && !replacedLineIds.includes(product.id)"
                    type="checkbox"
                    :checked="selectedProductsRupture.includes(product.id)"
                    :aria-label="`Sélectionner le produit en rupture ${product.reference || product.id}`"
                    @change="toggleProductRupture(product.id)"
                    class="w-6 h-6 mt-1 min-[601px]:mt-2 flex-shrink-0 text-blue-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                </div>
                <div class="text-sm text-black leading-tight pb-1">
                  Catégorie :
                  <span>{{ product.categorie || "-" }}</span>
                </div>
                <span v-if="replacedLineIds.includes(product.id)" class="block text-sm text-indigo-700 py-2">Proposé / remplacé — voir la proposition</span>
              </div>
              <div class="py-2 pr-3 pl-0 min-[601px]:p-3 hover:bg-red-100 transition-colors">
                <!-- Layout horizontal : Image à gauche, Infos à droite -->
                <div class="flex gap-3">
                  <!-- Image -->
                  <div
                    class="w-28 h-36 min-[360px]:w-32 min-[360px]:h-44 min-[601px]:w-40 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded border border-gray-200 overflow-hidden"
                  >
                    <img
                      v-if="getImageUrlForProduct(product)"
                      :src="getImageUrlForProduct(product)"
                      :alt="product.reference"
                      class="w-full h-full object-contain min-[601px]:object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      @error="handleImageError"
                      @click="
                        openImageModal(
                          getImageUrlForProduct(product),
                          product.reference,
                        )
                      "
                    />
                    <i v-else class="fas fa-tshirt text-red-300 text-lg"></i>
                  </div>

                  <!-- Infos principales -->
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-black mt-0.5 mb-2 flex items-center gap-1.5 flex-wrap">
                      Couleur:
                      <span
                        v-if="hasOfficialColorSwatch(product)"
                        class="color-swatch-dot inline-block w-3.5 h-3.5 rounded-full flex-shrink-0"
                        :style="getOfficialColorSwatchStyle(product)"
                        :title="product.colorFR || product.color || ''"
                      ></span>
                      {{ product.colorFR || product.color || "-" }}
                    </p>

                    <!-- Quantité / Tailles -->
                    <div class="mb-2">
                      <div
                        v-if="getProductSizeRows(product).length"
                        class="mb-2 flex flex-wrap justify-start gap-x-3 gap-y-1"
                      >
                        <div
                          v-for="row in getProductSizeRows(product)"
                          :key="row.label"
                          class="flex flex-col items-center gap-0.5"
                        >
                          <span class="text-sm text-black">{{
                            row.label
                          }}</span>
                          <span class="text-sm font-bold text-black">{{
                            row.qty
                          }}</span>
                        </div>
                      </div>
                      <div class="text-sm text-black">
                        <div class="mb-0.5">Qté commandée</div>
                        <div class="font-medium">
                          {{ getOrderedQuantityLabel(product) }}
                        </div>
                      </div>
                    </div>

                    <!-- Prix HT et total -->
                    <div class="grid grid-cols-2 gap-2 text-sm text-black">
                      <div class="min-w-0 break-words">
                        <div class="mb-0.5">Prix HT:</div>
                        <div class="flex flex-col items-start gap-0.5 font-semibold">
                          <span
                            v-if="product.hasRemise && product.priceUnitNormal"
                            class="line-through font-normal"
                            >{{ formatPrice(product.priceUnitNormal) }}€</span
                          >
                          <span>{{ formatPrice(product.priceUnit || 0) }}€</span>
                        </div>
                      </div>
                      <div class="min-w-0 break-words">
                        <div class="mb-0.5">Total:</div>
                        <span class="font-semibold"
                          >{{ formatPrice(product.priceLineHT || product.price || 0) }}€</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Résumé des produits en rupture (comme l'ancien système) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 mt-4">
          <!-- Tableau Prix -->
          <div class="bg-gray-50 p-2 rounded text-sm">
            <div class="space-y-1">
              <div class="flex justify-between">
                <span class="text-black">Prix total (€) :</span>
                <span class="text-black">{{
                  formatPrice(totalRuptureHT)
                }}</span>
              </div>
              <div v-if="!isEtranger" class="flex justify-between">
                <span class="text-black">TVA ({{ tvaRate * 100 }}%) (€) :</span>
                <span class="text-black">{{
                  formatPrice(tvaAmountRupture || 0)
                }}</span>
              </div>
              <div v-if="!isEtranger" class="flex justify-between">
                <span class="text-black">Prix total (€ TTC) :</span>
                <span class="text-black">{{
                  formatPrice(totalRuptureTTC || 0)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Tableau Quantité/Poids -->
          <div class="bg-gray-50 p-2 rounded text-sm">
            <div class="space-y-1">
              <div class="flex justify-between">
                <span class="text-black">Quantité totale :</span>
                <span class="text-black">{{ totalRuptureQuantite }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-black">Poids total (kg):</span>
                <span class="text-black">{{
                  formatWeight(totalRupturePoids)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bouton Réactiver - seulement si statut == 1 (en attente) -->
        <div class="mt-3">
          <button type="button" :disabled="!canProposeReplacement || !productsRupture.some(p => !replacedLineIds.includes(p.id))" :title="replacementUnavailableReason || undefined" class="mr-3 px-4 py-2 rounded-md bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed" @click="replacementPanel?.open()">Proposer des remplacements</button>
          <button
            v-if="canReactivate"
            @click="openReactiveModal"
            :disabled="reactivableSelection.length === 0"
            :class="[
              'px-4 py-2 rounded-md transition-colors',
              reactivableSelection.length === 0
                ? 'bg-gray-300 text-black cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700',
            ]"
          >
            Réactiver
          </button>
        </div>
      </div>

      <!-- Modal de confirmation pour la réactivation -->
      <ModalComp
        v-model:isVisible="showReactiveModal"
        title="Réactiver"
        content="Êtes-vous sûr de vouloir réactiver des produits de cette commande ?"
        confirm-text="Confirmer"
        cancel-text="Annuler"
        size="sm"
        @confirm="confirmReactive"
      >
        <template #footer>
          <button
            @click="showReactiveModal = false"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            type="button"
          >
            Annuler
          </button>
          <button
            @click="confirmReactive"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            type="button"
          >
            Confirmer
          </button>
        </template>
      </ModalComp>

      <!-- Modal de division -->
      <ModalComp
        v-model:isVisible="showDiviserModal"
        title="Diviser les paquets"
        size="lg"
      >
        <template #body>
          <div v-if="lignesADiviser.length > 0" class="space-y-4">
            <div
              v-for="ligne in lignesADiviser"
              :key="ligne.id_ligne"
              class="border rounded-lg p-4"
            >
              <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
                <h4 class="font-semibold text-black">{{ ligne.reference }}</h4>
                <div
                  class="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-end"
                >
                  <span class="text-sm font-medium text-black"
                    >Prix HT unitaire:
                    {{ formatPrice(ligne.prixUnitaire) }}€</span
                  >
                  <button
                    @click="remplirRuptureTotale(ligne)"
                    :disabled="isRuptureTotale(ligne)"
                    :class="[
                      'w-full px-3 py-2 text-sm rounded-lg transition-colors sm:w-auto sm:py-1.5',
                      isRuptureTotale(ligne)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700',
                    ]"
                    type="button"
                  >
                    Tout mettre en rupture
                  </button>
                </div>
              </div>
              <div class="hidden overflow-x-auto lg:block">
                <table class="min-w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-gray-50">
                      <th class="border px-2 py-1 text-left text-black">
                        Déclinaison
                      </th>
                      <th
                        v-for="(label, index) in ligne.declinaisonsLabels"
                        :key="index"
                        class="border px-2 py-1 text-center text-black"
                      >
                        {{ label || `Q${index + 1}` }}
                      </th>
                      <th class="border px-2 py-1 text-center text-black">
                        Prix HT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="border px-2 py-1 font-medium text-black">
                        Commande
                      </td>
                      <td
                        v-for="(qty, index) in ligne.base_qt"
                        :key="index"
                        class="border px-2 py-1"
                      >
                        <input
                          type="number"
                          :value="ligne.base_qt[index]"
                          readonly
                          class="w-full px-2 py-1 border rounded text-center bg-gray-50 cursor-not-allowed"
                        />
                      </td>
                      <td class="border px-2 py-1">
                        <input
                          type="number"
                          :value="ligne.base_prixligne"
                          step="0.01"
                          readonly
                          class="w-full px-2 py-1 border rounded text-center bg-gray-50 cursor-not-allowed"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td class="border px-2 py-1 font-medium text-black">
                        Rupture
                      </td>
                      <td
                        v-for="(qty, index) in ligne.qt"
                        :key="index"
                        class="border px-2 py-1"
                      >
                        <input
                          type="number"
                          v-model.number="ligne.qt[index]"
                          @input="updateDiviserPrix(ligne)"
                          :min="0"
                          :max="ligne.quantitesOriginales[index]"
                          class="w-full px-2 py-1 border rounded text-center"
                        />
                      </td>
                      <td class="border px-2 py-1">
                        <input
                          type="number"
                          v-model.number="ligne.prixligne"
                          @input="updateDiviserManualPrice(ligne)"
                          step="0.01"
                          min="0"
                          :max="ligne.prixOriginal"
                          :readonly="!isAdmin"
                          :class="[
                            'w-full px-2 py-1 border rounded text-center',
                            !isAdmin
                              ? 'bg-gray-50 cursor-not-allowed'
                              : 'bg-white',
                          ]"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Saisie mobile : les déclinaisons restent lisibles sans tableau compressé -->
              <div class="space-y-3 lg:hidden">
                <div
                  v-for="(label, index) in ligne.declinaisonsLabels"
                  :key="index"
                  class="rounded-lg border border-gray-200 bg-gray-50 p-3"
                >
                  <div class="mb-2 font-semibold text-black">
                    {{ label || `Q${index + 1}` }}
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <label class="block min-w-0">
                      <span class="mb-1 block text-xs font-medium text-gray-600">
                        Commande
                      </span>
                      <input
                        type="number"
                        :value="ligne.base_qt[index]"
                        readonly
                        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-base text-black cursor-not-allowed"
                      />
                    </label>
                    <label class="block min-w-0">
                      <span class="mb-1 block text-xs font-medium text-red-700">
                        Rupture
                      </span>
                      <input
                        type="number"
                        v-model.number="ligne.qt[index]"
                        @input="updateDiviserPrix(ligne)"
                        :min="0"
                        :max="ligne.quantitesOriginales[index]"
                        inputmode="numeric"
                        class="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-center text-base text-black focus:border-red-500 focus:ring-2 focus:ring-red-200"
                      />
                    </label>
                  </div>
                </div>

                <div class="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 sm:grid-cols-2">
                  <label class="block min-w-0">
                    <span class="mb-1 block text-xs font-medium text-gray-600">
                      Prix commande HT
                    </span>
                    <input
                      type="number"
                      :value="ligne.base_prixligne"
                      step="0.01"
                      readonly
                      class="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-right text-base text-black cursor-not-allowed"
                    />
                  </label>
                  <label class="block min-w-0">
                    <span class="mb-1 block text-xs font-medium text-red-700">
                      Prix rupture HT
                    </span>
                    <input
                      type="number"
                      v-model.number="ligne.prixligne"
                      @input="updateDiviserManualPrice(ligne)"
                      step="0.01"
                      min="0"
                      :max="ligne.prixOriginal"
                      :readonly="!isAdmin"
                      :class="[
                        'w-full rounded-lg border px-3 py-2 text-right text-base text-black',
                        !isAdmin
                          ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'border-red-300 bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200',
                      ]"
                    />
                  </label>
                </div>
              </div>
              <div v-if="ligne.error" class="mt-2 text-red-600 text-sm">
                {{ ligne.error }}
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 text-black">
            Aucune ligne sélectionnée
          </div>
        </template>
        <template #footer>
          <button
            @click="showDiviserModal = false"
            :disabled="dividing"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            type="button"
          >
            Annuler
          </button>
          <button
            @click="confirmDiviser"
            :disabled="dividing || !canConfirmDiviser"
            :aria-busy="dividing"
            :class="[
              'inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-60',
              canConfirmDiviser
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-black cursor-not-allowed',
            ]"
            type="button"
          >
            <i v-if="dividing" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            {{ dividing ? 'Traitement…' : 'Sauvegarder' }}
          </button>
        </template>
      </ModalComp>
    </div>
  </DataCard>

  <!-- Action contextuelle, positionnée au-dessus de la footbar sur mobile -->
  <div
    v-if="canMarkRupture && selectedProducts.length > 0 && !showDiviserModal"
    class="fixed bottom-[77px] left-3 right-3 z-[900] flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-white p-3 shadow-xl md:bottom-6 md:left-auto md:right-6 md:w-[420px]"
  >
    <div class="min-w-0">
      <div class="text-sm font-semibold text-black">
        {{ selectedProducts.length }}
        {{
          selectedProducts.length > 1
            ? "références sélectionnées"
            : "référence sélectionnée"
        }}
      </div>
      <div class="text-xs text-gray-500">Créer une rupture de stock</div>
    </div>
    <button
      type="button"
      class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:ring-2 focus:ring-red-300 disabled:opacity-60"
      :disabled="dividing"
      :aria-busy="dividing"
      @click="openDiviserModal"
    >
      <i :class="dividing ? 'fas fa-spinner fa-spin' : 'fas fa-box-open'" aria-hidden="true"></i>
      {{ dividing ? 'Traitement…' : 'Rupture' }}
    </button>
  </div>

  <!-- Modal pour afficher l'image en grand -->
  <div
    v-if="showImageModal"
    class="fixed inset-0 z-[9999] overflow-y-auto"
    @wheel.passive="() => {}"
  >
    <div
      class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center"
    >
      <!-- Overlay -->
      <div
        class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        @click="closeImageModal"
      ></div>

      <!-- Modal -->
      <div
        class="my-20 inline-block w-full text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-hidden flex flex-col max-w-md p-6"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-4 flex-shrink-0">
          <h3 class="text-lg font-medium text-black">
            {{ selectedImageAlt }}
          </h3>
          <button
            @click="closeImageModal"
            class="text-black hover:text-black transition-colors"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="mb-4 flex-1 flex items-center justify-center">
          <img
            v-if="selectedImageUrl"
            :src="selectedImageUrl"
            :alt="selectedImageAlt"
            class="max-w-full max-h-96 object-contain rounded-lg"
            @click.stop
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation de suppression d'un commentaire admin -->
  <ConfirmModal
    :show="showDeleteCommentModal"
    title="Supprimer le commentaire"
    message="Voulez-vous vraiment supprimer ce commentaire ?"
    confirmText="Supprimer"
    confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
    :showAttentionIcon="true"
    @confirm="confirmDeleteAdminCommentaire"
    @cancel="cancelDeleteAdminCommentaire"
  />
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type ComputedRef,
} from "vue";
import { ConfirmModal, DataCard, ModalComp } from "@/components";
import { useNotification } from "@/composables/useNotification";
import type {
  OrderProduct,
  OrderSummaryData,
  OrderDetailResponse,
  LigneDiviser,
} from "@/types";
import {
  mapToProducts,
  mapToProductsRupture,
  mapToOrderSummary,
  getReplacementMessage,
} from "@/utils/orderMapper";
import { getColorSwatchStyle } from "@/utils/colorFamilies";
import {
  isEtranger as checkIsEtranger,
  calculateTVARate,
} from "@/utils/tvaUtils";
import { actionReactive, actionDiviser } from "@/api/orders";
import { useAuthStore } from "@/stores/auth";
import { getProductPhotosBatch } from "@/api/shooting";
import { getApiBaseUrl, getProductImageUrl } from "@/config/api";
import OrderReplacements from "./OrderReplacements.vue";

// Props
interface Props {
  orderId: number;
  loading?: boolean;
  class?: string;
  collapsible?: boolean;
  disableAction?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  collapsible: false,
  disableAction: false,
});

const emit = defineEmits<{
  collapsed: [value: boolean];
  replacementConfirmation: [value: boolean];
}>();

const isDesktop = ref(
  typeof window !== "undefined" ? window.innerWidth > 600 : false,
);

const isCollapsed = ref(
  typeof window !== "undefined" ? window.innerWidth <= 600 : true,
);

const updateViewportState = () => {
  if (typeof window === "undefined") return;
  isDesktop.value = window.innerWidth > 600;
};

// Une fois l'animation d'ouverture terminée, on retire le plafond de hauteur
// (max-h-none) pour éviter que les grosses commandes soient tronquées par
// overflow-hidden. À la fermeture, on rétablit le plafond pour animer.
const isExpanded = ref(
  typeof window !== "undefined" ? window.innerWidth > 600 : false,
);

const toggleCollapse = () => {
  if (!props.collapsible) return;
  isCollapsed.value = !isCollapsed.value;
  if (isCollapsed.value) {
    // On replafonne immédiatement pour pouvoir animer la fermeture
    isExpanded.value = false;
  }
  emit("collapsed", isCollapsed.value);
};

const onContentTransitionEnd = (event: TransitionEvent) => {
  // Ne réagir qu'à la fin de la transition de hauteur du conteneur lui-même
  if (event.propertyName !== "max-height") return;
  if (event.target !== event.currentTarget) return;
  if (!isCollapsed.value) {
    isExpanded.value = true;
  }
};

// Récupérer les données depuis le parent via inject
const orderDetailDataInjected = inject<
  ComputedRef<{ value: OrderDetailResponse | null }>
>(
  "orderDetailData",
  computed(() => ({ value: null })),
);

// Simplifier l'accès aux données
const orderDetailData = computed(() => orderDetailDataInjected.value.value);

const orderDetailsTitle = computed(() => {
  const groupName = orderDetailData.value?.id_commande_groupe_name;

  if (groupName) return `Détails de la commande ${groupName}`;
  return "Détails de la commande";
});

onMounted(() => {
  updateViewportState();
  window.addEventListener("resize", updateViewportState);
});

onUnmounted(() => {
  if (typeof window === "undefined") return;
  window.removeEventListener("resize", updateViewportState);
});

// Injecter la fonction de rechargement depuis le parent
const reloadOrderData = inject<(() => Promise<void>) | undefined>(
  "reloadOrderData",
  undefined,
);

// reloadOrderData sera utilisé si disponible, sinon fallback sur window.location.reload()

// État pour les produits sélectionnés (checkboxes) - produits actifs
const selectedProducts = ref<number[]>([]);

// État pour les produits en rupture sélectionnés (checkboxes)
const selectedProductsRupture = ref<number[]>([]);
const replacementPanel = ref<{ open: () => Promise<void> } | null>(null);
const replacedLineIds = ref<number[]>([]);
const canProposeReplacement = computed(() => Boolean(orderDetailData.value && (authStore.isImpersonating || Number(orderDetailData.value.statut) === 1)));
const replacementUnavailableReason = computed(() => {
  if (!orderDetailData.value || canProposeReplacement.value) return '';
  if (Number(orderDetailData.value.statut) === 7) return 'Cette commande est déjà prête pour le pickup des colis. Seul un administrateur peut proposer un remplacement à ce stade.';
  return 'Le vendeur peut proposer un remplacement uniquement avant la confirmation de la commande. Contactez le SAV pour vérifier la situation de cette commande.';
});
const onReplacementChanged = async () => {
  selectedProductsRupture.value = [];
  await reloadOrderData?.();
};

// Notifications
const { success, error } = useNotification();

// États pour les modals de confirmation
const showReactiveModal = ref(false);
const showDiviserModal = ref(false);
const dividing = ref(false);

// Modal pour l'image agrandie
const showImageModal = ref(false);
const selectedImageUrl = ref<string>("");
const selectedImageAlt = ref<string>("");

// Cache des catégories pour éviter les appels répétés

// Les catégories viennent maintenant directement du backend !
// Plus besoin d'enrichissement complexe

// Données mappées depuis l'API - produits actifs
// Données mappées depuis l'API (base)
const rawProducts = computed<OrderProduct[]>(() => {
  if (!orderDetailData.value) return [];
  return mapToProducts(orderDetailData.value);
});

const products = computed<OrderProduct[]>(() => {
  if (!orderDetailData.value) return [];
  return mapToProducts(orderDetailData.value).sort((a, b) => {
    // Trier par catégorie (ordre alphabétique)
    const catA = a.categorie || "";
    const catB = b.categorie || "";
    return catA.localeCompare(catB);
  });
});

// Grouper les produits par reference_base
interface ProductGroup {
  referenceBase: string;
  products: OrderProduct[];
  totalPieces: number;
}

const productGroups = computed<ProductGroup[]>(() => {
  const grouped = new Map<string, OrderProduct[]>();

  for (const product of products.value) {
    const base = product.referenceBase || product.reference || "unknown";
    if (!grouped.has(base)) {
      grouped.set(base, []);
    }
    grouped.get(base)!.push(product);
  }

  return Array.from(grouped.entries()).map(([referenceBase, prods]) => ({
    referenceBase,
    products: prods,
    totalPieces: prods.reduce((sum, p) => sum + (p.quantite_total || 0), 0),
  }));
});

// Données mappées depuis l'API - produits en rupture
const rawProductsRupture = computed<OrderProduct[]>(() => {
  if (!orderDetailData.value) return [];
  return mapToProductsRupture(orderDetailData.value);
});

// Produits en rupture avec catégories venant directement du backend
const productsRupture = computed<OrderProduct[]>(() => {
  if (!orderDetailData.value) return [];
  return mapToProductsRupture(orderDetailData.value).sort((a, b) => {
    // Trier par catégorie (ordre alphabétique)
    const catA = a.categorie || "";
    const catB = b.categorie || "";
    return catA.localeCompare(catB);
  });
});

const orderSummary = computed<OrderSummaryData | null>(() => {
  if (!orderDetailData.value) return null;
  return mapToOrderSummary(orderDetailData.value);
});

const replacementMessage = computed<string | null>(() => {
  if (!orderDetailData.value) return null;
  return getReplacementMessage(orderDetailData.value);
});

const remises = computed(() => {
  if (!orderDetailData.value) return null;
  return orderDetailData.value.remises || null;
});

const remisesCommandes = computed(() => {
  if (!remises.value) return [];
  return remises.value.filter((r) => !r.isFraisPort);
});

const remisesFraisPort = computed(() => {
  if (!remises.value) return [];
  return remises.value.filter((r) => r.isFraisPort);
});

// Vérifier si on peut modifier la commande
// Ancien système: buttonRuptureDiviser = (statut == "1") || (admin && disable_action)
const canMarkRupture = computed(() => {
  if (!orderDetailData.value) return false;
  if (orderDetailData.value.statut === 1) return true;
  // Un admin (impersonation EFP) peut toujours toucher rupture / diviser
  return authStore.isImpersonating && props.disableAction === true;
});

// Les lignes proposées sont verrouillées individuellement via replacedLineIds.
const canReactivate = computed(() => canMarkRupture.value);
const reactivableSelection = computed(() => selectedProductsRupture.value.filter(id =>
  productsRupture.value.some(product => product.id === id) && !replacedLineIds.value.includes(id),
));

// Calculs pour les prix avec réduction
const montantTotal = computed(() => orderDetailData.value?.montantTotal || 0);
const montantCA = computed(() => orderDetailData.value?.montantCA || 0);
const montantApresRemise = computed(
  () =>
    orderDetailData.value?.montantApresRemise ||
    orderDetailData.value?.montantTotal ||
    0,
);

// Déterminer si la commande est avant 2026 (comme dans l'ancien PHP)
const yearBefore2026 = computed(() => {
  const date =
    orderDetailData.value?.dateCommande || orderDetailData.value?.dateCreation;
  if (!date) return true;
  return new Date(date).getFullYear() < 2026;
});

// PHP: if (($yearBefore2026 && $commandeObj->montantTotal != $commandeObj->montantApresRemise) || (!$yearBefore2026 && ($commandeObj->montantCA != $commandeObj->montantTotal)))

const hasReduction = computed(() => {
  if (yearBefore2026.value) {
    return montantTotal.value !== montantApresRemise.value;
  } else {
    const effectiveMontantCA = montantCA.value
      ? montantCA.value
      : montantApresRemise.value;
    return effectiveMontantCA !== montantTotal.value;
  }
});

// Montant après remise pour affichage (comme PHP: $commandeObj->montantCA?$commandeObj->montantCA:$commandeObj->montantApresRemise)
const montantApresRemiseDisplay = computed(() =>
  montantCA.value ? montantCA.value : montantApresRemise.value,
);

// Remise HT (comme PHP: $commandeObj->montantTotal - ($commandeObj->montantCA?$commandeObj->montantCA:$commandeObj->montantApresRemise))
const reductionAmount = computed(
  () => montantTotal.value - montantApresRemiseDisplay.value,
);

// Vérifier si le client est étranger (ne paye pas la TVA)
const isEtranger = computed(() => {
  if (!orderDetailData.value) return false;
  return checkIsEtranger(orderDetailData.value);
});

// Calculer le taux de TVA selon la date de la commande
const tvaRate = computed(() => {
  if (!orderDetailData.value) return 0.2;
  return calculateTVARate(
    orderDetailData.value.dateCommande || orderDetailData.value.dateCreation,
  );
});

//  PHP: ($commandeObj->montantCA?$commandeObj->montantCA:$commandeObj->montantApresRemise) * $autoTva
const tvaAmount = computed(() => {
  if (isEtranger.value) return 0;
  return montantApresRemiseDisplay.value * tvaRate.value;
});

const totalTTC = computed(() => {
  if (isEtranger.value) return montantApresRemiseDisplay.value;
  return montantApresRemiseDisplay.value * (1 + tvaRate.value);
});

// ------------------ Images: charger depuis uploads comme listproduit ------------------
const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);

// ------------------ Commentaires admin ------------------
interface AdminCommentaire {
  id_commande_commentaire: number;
  prenom: string | null;
  commentaire: string;
  date_creation: string;
  canDelete?: boolean;
}

const adminCommentaires = ref<AdminCommentaire[]>([]);
const newAdminComment = ref("");
const savingAdminComment = ref(false);
const showDeleteCommentModal = ref(false);
const commentToDelete = ref<number | null>(null);

const unescapeComment = (value: string): string => {
  if (!value) return value;
  return value.replace(/\\(['"\\])/g, "$1");
};

const formatCommentDate = (value: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString("fr-FR");
};

const adminCommentApi = async (path: string, body: unknown): Promise<unknown> => {
  const response = await fetch(`${getApiBaseUrl()}/api/admin-efp/${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

const loadAdminCommentaires = async () => {
  if (!isAdmin.value || !props.orderId) return;
  try {
    const data = (await adminCommentApi("ruptures/commentaires/get", {
      idCommande: props.orderId,
    })) as AdminCommentaire[] | null;
    adminCommentaires.value = Array.isArray(data)
      ? data.map((c) => ({ ...c, commentaire: unescapeComment(c.commentaire) }))
      : [];
  } catch (e) {
    console.error("Erreur lors du chargement des commentaires admin:", e);
  }
};

const saveAdminCommentaire = async () => {
  const text = newAdminComment.value.trim();
  if (!text || savingAdminComment.value) return;
  savingAdminComment.value = true;
  try {
    const idCommandeName =
      orderDetailData.value?.id_commande_name ?? String(props.orderId);
    const inserted = (await adminCommentApi("ruptures/commentaires/save", {
      idCommande: props.orderId,
      idCommandeName,
      commentaire: text,
    })) as AdminCommentaire | null;
    if (inserted) {
      adminCommentaires.value.push({
        ...inserted,
        commentaire: unescapeComment(inserted.commentaire),
      });
      newAdminComment.value = "";
    }
  } catch (e) {
    error("Erreur lors de l'enregistrement du commentaire");
    console.error(e);
  } finally {
    savingAdminComment.value = false;
  }
};

const askDeleteAdminCommentaire = (idCommentaire: number) => {
  commentToDelete.value = idCommentaire;
  showDeleteCommentModal.value = true;
};

const cancelDeleteAdminCommentaire = () => {
  showDeleteCommentModal.value = false;
  commentToDelete.value = null;
};

const confirmDeleteAdminCommentaire = async () => {
  const idCommentaire = commentToDelete.value;
  showDeleteCommentModal.value = false;
  commentToDelete.value = null;
  if (idCommentaire == null) return;
  try {
    await adminCommentApi("ruptures/commentaires/delete", {
      idCommande: props.orderId,
      idCommentaire,
    });
    adminCommentaires.value = adminCommentaires.value.filter(
      (c) => c.id_commande_commentaire !== idCommentaire,
    );
  } catch (e) {
    error("Erreur lors de la suppression du commentaire");
    console.error(e);
  }
};

watch(
  () => [isAdmin.value, props.orderId] as const,
  () => {
    if (isAdmin.value && props.orderId) {
      loadAdminCommentaires();
    } else {
      adminCommentaires.value = [];
    }
  },
  { immediate: true },
);

// Batch load product photos using the batch API
const batchLoadProductPhotos = async (productIds: number[]): Promise<void> => {
  if (productIds.length === 0) return;

  try {
    // Appeler l'API batch (retourne { success, photosByProduct })
    const { success, photosByProduct } = await getProductPhotosBatch(
      productIds,
    );

    if (!success || !photosByProduct) {
      return;
    }

    // Mettre à jour le cache et imageUrls avec les résultats
    const idVendeur = authStore.user?.id;
    productIds.forEach((pid) => {
      const photos = photosByProduct[pid];
      const first =
        Array.isArray(photos) && photos.length > 0 ? photos[0] : undefined;

      const absoluteUrl = first
        ? getProductImageUrl(first)
        : idVendeur
          ? getProductImageUrl(
              `/uploads/products/${idVendeur}/Produits/accueil/${pid}-c.jpg`,
            )
          : undefined;

      if (absoluteUrl) {
        imageCache.set(pid, absoluteUrl);
        imageUrls.value[pid] = absoluteUrl;
      }
    });
  } catch (error) {
    console.error("Erreur lors du chargement batch des photos:", error);
    // Pas de fallback : en cas d'erreur, on n'affiche rien
  }
};

// Cache URLs by productId (non‑reactive, for avoiding duplicate fetches)
const imageCache = new Map<number, string>();

// Reactive store to ensure the template updates when a URL is resolved
const imageUrls = ref<Record<number, string>>({});

const resolveImageUrl = (p: OrderProduct): string | undefined => {
  // Already present from API or cache
  if ((p as any).imageUrl) return (p as any).imageUrl || undefined;
  const pid = p.productId;
  if (!pid) return undefined;

  // Retourner depuis le cache (rempli par le batch)
  if (imageCache.has(pid)) return imageCache.get(pid);
  if (imageUrls.value[pid]) return imageUrls.value[pid];

  return undefined;
};

// Public getter used in template (returns cached URL from batch)
const getImageUrlForProduct = (p: OrderProduct): string | undefined => {
  const pid = p.productId;
  if (!pid) return undefined;

  // Lire depuis le store réactif (rempli par le batch)
  if (imageUrls.value[pid]) return imageUrls.value[pid];

  // Fallback au cache
  const cached = imageCache.get(pid);
  if (cached) return cached;

  // Pas de fallback : si pas de photo, on retourne undefined
  return undefined;
};

// Batch load images when product lists change
watch(
  products,
  async (list) => {
    // Récupérer tous les IDs de produits qui n'ont pas encore d'image en cache
    const productIds = list
      .map((p) => p.productId)
      .filter(
        (pid): pid is number => pid !== undefined && !imageCache.has(pid),
      );

    if (productIds.length > 0) {
      await batchLoadProductPhotos(productIds);
    }
  },
  { immediate: true },
);

// Batch load images pour les produits en rupture aussi
watch(
  productsRupture,
  async (list) => {
    const productIds = list
      .map((p) => p.productId)
      .filter(
        (pid): pid is number => pid !== undefined && !imageCache.has(pid),
      );

    if (productIds.length > 0) {
      await batchLoadProductPhotos(productIds);
    }
  },
  { immediate: true },
);

// Calculs pour les produits en rupture
// Montant HT avant remise de commande (somme des prixLigne sans remise de commande)
const totalRuptureHTAvantRemiseCommande = computed(() => {
  return productsRupture.value.reduce(
    (sum, p) => sum + (p.priceLineHT || p.price || 0),
    0,
  );
});

// Montant HT après remise de commande (utiliser montantRuptureHT du backend si disponible)
// Sinon, appliquer les remises en pourcentage seulement (comme l'ancien système pour corbeille)
const totalRuptureHT = computed(() => {
  // Si on a montantRuptureHT du backend, l'utiliser (déjà calculé avec remises)
  if (orderDetailData.value?.montantRuptureHT != null) {
    return orderDetailData.value.montantRuptureHT;
  }

  // Sinon, calculer depuis les produits et appliquer les remises en pourcentage seulement
  let total = totalRuptureHTAvantRemiseCommande.value;

  // Appliquer seulement les remises en pourcentage (comme l'ancien système pour corbeille)
  for (const remise of remisesCommandes.value) {
    if (remise.type === "pourcentage") {
      const pourcentage = Math.abs(
        parseFloat(remise.montant?.toString() || "0"),
      );
      total = total - (total * pourcentage) / 100;
    }
    // Les remises fixes (reduction) ne sont PAS appliquées aux produits en rupture dans l'ancien système
  }

  return parseFloat(Math.max(0, total).toFixed(2));
});

const hasReductionRupture = computed(() => {
  return (
    totalRuptureHTAvantRemiseCommande.value > 0 &&
    totalRuptureHT.value < totalRuptureHTAvantRemiseCommande.value
  );
});

const reductionAmountRupture = computed(() => {
  return totalRuptureHTAvantRemiseCommande.value - totalRuptureHT.value;
});

const tvaAmountRupture = computed(() => {
  if (isEtranger.value) return 0;
  return totalRuptureHT.value * tvaRate.value;
});

const totalRuptureTTC = computed(() => {
  if (isEtranger.value) return totalRuptureHT.value;
  return totalRuptureHT.value * (1 + tvaRate.value);
});

const totalRuptureQuantite = computed(() => {
  return productsRupture.value.reduce(
    (sum, p) => sum + (p.quantite_total || p.details || 0),
    0,
  );
});

const totalRupturePoids = computed(() => {
  return productsRupture.value.reduce(
    (sum, p) => sum + (p.weightTotal || p.weight || 0),
    0,
  );
});

// Méthodes
const formatPrice = (price: number) => {
  return price.toFixed(2);
};

const formatWeight = (weight: number) => {
  return weight.toFixed(2);
};

// Gérer les erreurs d'image
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = "none";
  const parent = img.parentElement;
  if (parent) {
    const icon = document.createElement("i");
    icon.className = "fas fa-tshirt text-black text-2xl";
    parent.appendChild(icon);
  }
};

// État pour la division
const lignesADiviser = ref<LigneDiviser[]>([]);

// Toggle la sélection d'un produit (actif)
const toggleProduct = (productId: number) => {
  const index = selectedProducts.value.indexOf(productId);
  if (index > -1) {
    selectedProducts.value.splice(index, 1);
  } else {
    selectedProducts.value.push(productId);
  }
};

// Toggle la sélection d'un produit en rupture
const toggleProductRupture = (productId: number) => {
  const index = selectedProductsRupture.value.indexOf(productId);
  if (index > -1) {
    selectedProductsRupture.value.splice(index, 1);
  } else {
    selectedProductsRupture.value.push(productId);
  }
};

// Ouvrir la modal de confirmation pour la réactivation
const openReactiveModal = () => {
  if (!canReactivate.value || reactivableSelection.value.length === 0) {
    return;
  }
  showReactiveModal.value = true;
};

// Ouvrir la modal de division
const openDiviserModal = () => {
  if (dividing.value || selectedProducts.value.length === 0) {
    return;
  }

  // Initialiser les lignes à diviser
  lignesADiviser.value = selectedProducts.value
    .map((productId) => {
      const product = products.value.find((p) => p.id === productId);
      if (!product) {
        return null;
      }

      // Récupérer les quantités originales
      const quantitesOriginales: number[] = [];
      const declinaisonsLabels: string[] = [];
      let maxDeclinaisons = 0;

      if (product.declinaisons && product.quantites) {
        // Trouver la dernière déclinaison avec une quantité > 0
        let lastIndexWithQty = 0;
        for (let i = 1; i <= 12; i++) {
          const qty =
            product.quantites[`q${i}` as keyof typeof product.quantites] || 0;
          if (qty > 0) {
            lastIndexWithQty = i;
          }
        }

        // Récupérer toutes les quantités jusqu'à la dernière avec quantité > 0
        for (let i = 1; i <= lastIndexWithQty; i++) {
          const qty =
            product.quantites[`q${i}` as keyof typeof product.quantites] || 0;
          quantitesOriginales.push(qty);
          const declLabel =
            product.declinaisons[
              `d${i}_FR` as keyof typeof product.declinaisons
            ] ||
            product.declinaisons[
              `d${i}_UK` as keyof typeof product.declinaisons
            ] ||
            "";
          declinaisonsLabels.push(declLabel);
          maxDeclinaisons = i;
        }

        // Si aucune quantité trouvée, utiliser quantite_total
        if (quantitesOriginales.length === 0) {
          quantitesOriginales.push(
            product.quantite_total || product.details || 0,
          );
          declinaisonsLabels.push("");
          maxDeclinaisons = 1;
        }
      } else {
        // Si pas de déclinaisons, une seule quantité
        quantitesOriginales.push(
          product.quantite_total || product.details || 0,
        );
        declinaisonsLabels.push("");
        maxDeclinaisons = 1;
      }

      // Initialiser base_qt avec les quantités originales et qt à 0
      const base_qt = [...quantitesOriginales];
      const qt = new Array(quantitesOriginales.length).fill(0);

      // Calculer le prix unitaire
      const quantiteTotalOriginale = quantitesOriginales.reduce(
        (sum, q) => sum + q,
        0,
      );
      const prixLigneHT = product.priceLineHT || product.price || 0;
      const prixUnitaire =
        quantiteTotalOriginale > 0
          ? prixLigneHT / quantiteTotalOriginale
          : product.priceUnit || 0;

      return {
        id_ligne: product.id,
        reference: product.reference || "",
        quantitesOriginales,
        base_qt,
        qt,
        base_prixligne: prixLigneHT,
        prixligne: 0,
        prixOriginal: prixLigneHT, // Sauvegarder le prix original
        prixUnitaire, // Prix unitaire HT
        maxDeclinaisons,
        declinaisonsLabels,
      } as LigneDiviser;
    })
    .filter((l): l is LigneDiviser => l !== null);

  if (lignesADiviser.value.length === 0) {
    return;
  }

  showDiviserModal.value = true;
};

// Ouvrir la modal d'image
const openImageModal = (imageUrl: string | undefined, alt: string) => {
  if (!imageUrl) return;
  selectedImageUrl.value = imageUrl;
  selectedImageAlt.value = alt;
  showImageModal.value = true;
};

// Fermer la modal d'image
const closeImageModal = () => {
  showImageModal.value = false;
  selectedImageUrl.value = "";
  selectedImageAlt.value = "";
};

// Mettre à jour les prix lors de la modification des quantités
const roundDiviserPrice = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const isRuptureTotale = (ligne: LigneDiviser) =>
  ligne.quantitesOriginales.every(
    (quantite, index) => ligne.qt[index] === quantite,
  );

const remplirRuptureTotale = (ligne: LigneDiviser) => {
  ligne.qt = [...ligne.quantitesOriginales];
  updateDiviserPrix(ligne);
};

const updateDiviserPrix = (ligne: LigneDiviser) => {
  // Calculer base_qt automatiquement : base_qt = quantitesOriginales - qt
  for (let i = 0; i < ligne.quantitesOriginales.length; i++) {
    const qt = ligne.qt[i] || 0;
    const original = ligne.quantitesOriginales[i] || 0;
    ligne.base_qt[i] = Math.max(0, original - qt);
  }

  // Vérifier que base_qt + qt = quantités originales
  let isValid = true;
  for (let i = 0; i < ligne.quantitesOriginales.length; i++) {
    const baseQt = ligne.base_qt[i] || 0;
    const qt = ligne.qt[i] || 0;
    const original = ligne.quantitesOriginales[i] || 0;

    if (baseQt + qt !== original) {
      isValid = false;
      ligne.error =
        "La somme des quantités doit être égale à la quantité originale";
      break;
    }
  }

  if (isValid) {
    ligne.error = undefined;

    // Calculer les prix proportionnellement
    const quantiteTotalRupture = ligne.qt.reduce((sum, q) => sum + q, 0);
    const quantiteTotalOriginale = ligne.quantitesOriginales.reduce(
      (sum, q) => sum + q,
      0,
    );

    if (quantiteTotalOriginale > 0) {
      // Utiliser le prix original pour calculer le prix unitaire
      const prixUnitaire = ligne.prixOriginal / quantiteTotalOriginale;
      ligne.prixligne = roundDiviserPrice(
        prixUnitaire * quantiteTotalRupture,
      );
      ligne.base_prixligne = roundDiviserPrice(
        ligne.prixOriginal - ligne.prixligne,
      );
    }
  }
};

// Comme dans l'ancien back-office, un administrateur peut ajuster le prix
// de la ligne en rupture. Le prix de la ligne restante est recalculé afin
// que la somme reste toujours égale au prix original.
const updateDiviserManualPrice = (ligne: LigneDiviser) => {
  if (!isAdmin.value) return;

  let prixRupture = Number(ligne.prixligne);
  if (!Number.isFinite(prixRupture)) {
    prixRupture = 0;
  }

  prixRupture = Math.min(Math.max(prixRupture, 0), ligne.prixOriginal);
  ligne.prixligne = roundDiviserPrice(prixRupture);
  ligne.base_prixligne = roundDiviserPrice(
    ligne.prixOriginal - ligne.prixligne,
  );
};

const isSinglePiecePriceAdjustment = (ligne: LigneDiviser) => {
  const originalQty = ligne.quantitesOriginales.reduce(
    (sum, qty) => sum + qty,
    0,
  );
  const ruptureQty = ligne.qt.reduce((sum, qty) => sum + qty, 0);
  return originalQty === 1 && ruptureQty === 0 && ligne.prixligne > 0;
};

const getLignesADiviserModifiees = () =>
  lignesADiviser.value.filter((ligne) => {
    const ruptureQty = ligne.qt.reduce((sum, quantite) => sum + quantite, 0);
    return ruptureQty > 0 || isSinglePiecePriceAdjustment(ligne);
  });

// Vérifier si on peut confirmer la division
const canConfirmDiviser = computed(() => {
  const lignesModifiees = getLignesADiviserModifiees();
  if (lignesModifiees.length === 0) return false;

  return lignesModifiees.every((ligne) => {
    if (ligne.error) return false;
    const quantiteTotalBase = ligne.base_qt.reduce(
      (sum, quantite) => sum + quantite,
      0,
    );
    const prixTotal = roundDiviserPrice(
      ligne.base_prixligne + ligne.prixligne,
    );
    return (
      (quantiteTotalBase === 0
        ? ligne.base_prixligne === 0
        : ligne.base_prixligne > 0) &&
      ligne.prixligne > 0 &&
      prixTotal === roundDiviserPrice(ligne.prixOriginal)
    );
  });
});

// Confirmer la division
const confirmDiviser = async () => {
  if (dividing.value || !canConfirmDiviser.value) {
    return;
  }

  dividing.value = true;
  try {
    const lignes = getLignesADiviserModifiees().map((ligne) => ({
      id_ligne: ligne.id_ligne,
      base_qt: ligne.base_qt,
      qt: ligne.qt,
      base_prixligne: ligne.base_prixligne,
      prixligne: ligne.prixligne,
    }));

    const result = await actionDiviser(props.orderId, lignes);

    if (result) {
      success("Les lignes ont été divisées avec succès");
      // Fermer la modal
      showDiviserModal.value = false;
      // Réinitialiser la sélection
      selectedProducts.value = [];
      lignesADiviser.value = [];
      // Recharger les données après un court délai pour laisser le temps de voir le toast
      setTimeout(async () => {
        if (reloadOrderData && typeof reloadOrderData === "function") {
          await reloadOrderData();
        } else {
          console.warn(
            "[OrderDetailsSection] reloadOrderData non disponible, utilisation du fallback",
          );
          window.location.reload();
        }
      }, 1500);
    } else {
      error("L'échec de l'action, merci de recommencer.");
    }
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Une erreur est survenue. Merci de réessayer.";
    error(errorMessage);
  } finally {
    dividing.value = false;
  }
};

// Confirmer la réactivation
const confirmReactive = async () => {
  if (!canReactivate.value || reactivableSelection.value.length === 0) return;
  const ids = [...reactivableSelection.value];
  showReactiveModal.value = false;

  try {
    // Appeler l'API
    const result = await actionReactive(
      props.orderId,
      ids,
    );

    if (result) {
      success("Les produits ont été réactivés avec succès");
      // Réinitialiser la sélection
      selectedProductsRupture.value = [];
      // Recharger les données après un court délai pour laisser le temps de voir le toast
      setTimeout(async () => {
        if (reloadOrderData && typeof reloadOrderData === "function") {
          await reloadOrderData();
        } else {
          // Fallback si la fonction n'est pas disponible
          window.location.reload();
        }
      }, 1500);
    } else {
      error("L'échec de l'action, merci de recommencer.");
    }
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Une erreur est survenue. Merci de réessayer.";
    error(errorMessage);
  }
};

// Affichage tailles : S / M / L au-dessus, quantités en dessous
const getProductSizeRows = (
  product: OrderProduct,
): Array<{ label: string; qty: number }> => {
  if (!product.declinaisons || !product.quantites) return [];

  const decl = product.declinaisons;
  const qty = product.quantites;
  const rows: Array<{ label: string; qty: number }> = [];

  for (let i = 1; i <= 12; i++) {
    const quantite = Number(qty[`q${i}` as keyof typeof qty] || 0);
    if (quantite <= 0) continue;
    const declLabel =
      decl[`d${i}_FR` as keyof typeof decl] ||
      decl[`d${i}_UK` as keyof typeof decl] ||
      "";
    if (!declLabel) continue;
    rows.push({ label: String(declLabel), qty: quantite });
  }

  return rows;
};

const hasOfficialColorSwatch = (product: OrderProduct): boolean =>
  Number(product.colorOfficiel) === 1;

const getOfficialColorSwatchStyle = (
  product: OrderProduct,
): Record<string, string> => {
  const name = product.colorFR || product.color || "";
  const hex = (product.colorHex || "").trim();
  if (hex.includes("-")) {
    const parts = hex
      .split("-")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => (part.startsWith("#") ? part : `#${part}`));
    if (parts.length >= 2) {
      const slice = 100 / parts.length;
      const stops = parts
        .map((color, i) => {
          const from = Math.round(i * slice);
          const to = Math.round((i + 1) * slice);
          return `${color} ${from}%, ${color} ${to}%`;
        })
        .join(", ");
      return { backgroundImage: `linear-gradient(90deg, ${stops})` };
    }
  }
  return getColorSwatchStyle(name, hex || null);
};

/** Pièces dans un pack (ex. 6 pour 2S 2M 2L). */
const getPackQuantity = (product: OrderProduct): number => {
  const packs = Number(product.quantite_pack || 0);
  const total = Number(product.quantite_total || product.details || 0);
  if (packs > 0 && total > 0) {
    return Math.round(total / packs);
  }
  const sizeTotal = getProductSizeRows(product).reduce(
    (sum, row) => sum + row.qty,
    0,
  );
  return sizeTotal || total || 0;
};

/** Affichage qté commandée selon le type de vente. */
const getOrderedQuantityLabel = (product: OrderProduct): string => {
  const venduPar = product.vendu_par || "couleurs";
  const nbPacks = Number(product.quantite_pack || 0);
  const total = Number(product.quantite_total || product.details || 0);
  const packSize = getPackQuantity(product);

  const formatPieces = (count: number): string =>
    count === 1 ? "1 pc" : `${count} pcs`;

  // Vendu par taille : pièces uniquement (détail dans la colonne Taille)
  if (venduPar === "tailles") {
    return formatPieces(total);
  }

  // couleurs / melangees : toujours format pack · pcs (même pack de 1)
  if (nbPacks <= 0) {
    return formatPieces(total);
  }

  const packWord = nbPacks === 1 ? "pack" : "packs";
  const pieceWord = packSize === 1 ? "pc" : "pcs";
  return `${nbPacks} ${packWord} · ${packSize} ${pieceWord}`;
};
</script>

<style scoped>
.color-swatch-dot {
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  background-repeat: no-repeat;
  background-size: 100% 100%;
}
</style>
